import { Banner, Button, Form, Password, popSuccessToast } from '@boltenergy-be/design-system'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ChangePasswordFormInputs } from './types'

import styles from './LoginDetailsForm.module.scss'
import EditLayout from 'layouts/edit-layout/EditLayout'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { routes } from 'types/routes'
import { newPassword } from 'store/auth/thunks'
import { useStoreDispatch } from 'hooks/store'
import { IS_PROD_TESTING } from 'constants/envs.ts'
import { useGetLanguage } from 'hooks/use-get-language'

const LoginDetailsForm = () => {
  // REDUX STORE
  const dispatch = useStoreDispatch()

  // React router
  const navigate = useNavigate()

  // State
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<boolean>(false)

  // i18n
  const { t } = useTranslation(['auth', 'user', 'errors', 'validation'])
  const language = useGetLanguage()

  // React hook form
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm<ChangePasswordFormInputs>({ mode: 'onBlur' })
  const watchNewPassword = watch('newPassword')

  /**
   * Handles the form submit after validation
   *
   * @param {ChangePasswordFormInputs} data
   */
  const onSubmit = async (data: ChangePasswordFormInputs) => {
    try {
      setError(false)
      setIsLoading(true)

      const response = await dispatch(newPassword({ currentPassword: data.currentPassword, newPassword: data.newPassword })).unwrap()

      if (response) {
        void navigate(routes.USER_LOGIN_DETAILS)
        popSuccessToast(
          t(($) => $.loginDetails.passwordChanged, {
            ns: 'user'
          })
        )
      } else {
        setError(true)
      }
    } catch (error) {
      setError(true)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <EditLayout
      title={t(($) => $.tabTitlesEdit.loginDetails, {
        ns: 'user'
      })}
    >
      <Form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
        <Password
          id="currentPassword"
          label={t(($) => $.loginDetails.currentPassword, {
            ns: 'user'
          })}
          {...register('currentPassword', {
            required: t(($) => $.required, {
              ns: 'validation'
            }),
            minLength: {
              value: 8,
              message: t(($) => $.invalid.passwordMinimum, {
                min: 8,
                ns: 'validation'
              })
            }
          })}
          error={errors?.currentPassword?.message}
          {...{ language }}
        />

        <Password
          id="newPassword"
          label={t(($) => $.loginDetails.newPassword, {
            ns: 'user'
          })}
          autoComplete="new-password"
          description={t(($) => $.loginDetails.minimal8Characters, {
            ns: 'user'
          })}
          {...register('newPassword', {
            required: t(($) => $.required, {
              ns: 'validation'
            }),
            minLength: {
              value: 8,
              message: t(($) => $.invalid.passwordMinimum, {
                min: 8,
                ns: 'validation'
              })
            }
          })}
          error={errors?.newPassword?.message}
          {...{ language }}
        />

        <Password
          id="confirmNewPassword"
          autoComplete="new-password"
          label={t(($) => $.loginDetails.repeatNewPassword, {
            ns: 'user'
          })}
          {...register('confirmNewPassword', {
            required: t(($) => $.required, {
              ns: 'validation'
            }),
            validate: (value) =>
              value === watchNewPassword ||
              t(($) => $.invalid.passwordConfirm, {
                ns: 'validation'
              })
          })}
          error={errors?.confirmNewPassword?.message}
          {...{ language }}
        />

        <Button isFullwidth className={styles.button} loading={isLoading} disabled={IS_PROD_TESTING}>
          {t(($) => $.buttons.resetPassword)}
        </Button>
        {error && (
          <Banner type="blocking">
            {t(($) => $.default, {
              ns: 'errors'
            })}
          </Banner>
        )}
      </Form>
    </EditLayout>
  )
}

export default LoginDetailsForm
